/**
 * FERP Portal - Primitive Form Modal (DRR-055 Compliant)
 *
 * Metadata-driven form component for creating/editing primitives.
 * All field definitions come from the backend FormSchema API.
 *
 * DESIGN:
 * - Backend FormSchema is the source of truth for fields (DRR-055)
 * - Display mode selector: Essential / Recommended / All
 * - No fallback — if backend schema not registered, shows "not available"
 * - Domain-aware filtering via backend FormSchemaOverride (per-tenant)
 *
 * Architecture:
 * ```
 * PrimitiveFormModal
 *   → usePrimitiveFormSchema(primitiveType, { displayMode })
 *   → useFormSchema({ subgraph: 'core', entityType })
 *   → schemaToFormDefinition()
 *   → FormRenderer (28+ field types)
 * ```
 *
 * USAGE:
 * <PrimitiveFormModal
 *   opened={opened}
 *   onClose={onClose}
 *   primitiveType="policy"
 *   mode="create"
 *   initialData={data}
 *   onSubmit={handleSubmit}
 * />
 */

import { useState, useCallback } from 'react';
import {
  Modal,
  Stack,
  Group,
  Button,
  Text,
  Alert,
  Skeleton,
  SegmentedControl,
  Badge,
  Tooltip,
  Divider,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconFilter,
  IconShieldCheck,
} from '@tabler/icons-react';
import type { PrimitiveType } from '@/config/primitive-metadata';
import type { PrimitiveItem } from '@/hooks/usePrimitiveData';
import type { PrimitiveMutationInput } from '@/hooks/usePrimitiveMutations';
import {
  usePrimitiveFormSchema,
  type FieldDisplayMode,
} from '@/hooks/usePrimitiveFormSchema';
import { FormRenderer } from '@/renderer';
import { mergeWithEntityData } from '@/components/UniversalEntityForm/schemaToFormDef';

// ============================================================================
// TYPES
// ============================================================================

interface PrimitiveFormModalProps {
  opened: boolean;
  onClose: () => void;
  primitiveType: PrimitiveType;
  mode: 'create' | 'edit' | 'view';
  initialData?: PrimitiveItem | null;
  onSubmit: (data: PrimitiveMutationInput) => Promise<void>;
  loading?: boolean;
  /** When true, renders form content without Modal wrapper (for use inside Drawer) */
  inline?: boolean;
}

// ============================================================================
// DISPLAY MODE CONFIG
// ============================================================================

const DISPLAY_MODE_DATA = [
  {
    value: 'essential',
    label: 'Essential',
  },
  {
    value: 'recommended',
    label: 'Recommended',
  },
  {
    value: 'all',
    label: 'All Fields',
  },
];

const DISPLAY_MODE_DESCRIPTIONS: Record<FieldDisplayMode, string> = {
  essential: 'Only required fields',
  recommended: 'Required + commonly used fields',
  all: 'All available fields from backend schema',
};

// ============================================================================
// COMPONENT
// ============================================================================

export function PrimitiveFormModal({
  opened,
  onClose,
  primitiveType,
  mode,
  initialData,
  onSubmit,
  loading = false,
  inline = false,
}: PrimitiveFormModalProps) {
  const [displayMode, setDisplayMode] = useState<FieldDisplayMode>('recommended');

  // Fetch form schema from backend (DRR-055) — no fallback
  const { formDefinition, loading: schemaLoading, error } =
    usePrimitiveFormSchema(primitiveType, { displayMode, mode });

  // Merge initial data for edit mode
  const formWithData = formDefinition && initialData
    ? mergeWithEntityData(formDefinition, initialData as Record<string, unknown>)
    : formDefinition;

  // Handle form submission
  const handleSubmit = useCallback(async (values: Record<string, unknown>) => {
    await onSubmit(values as PrimitiveMutationInput);
    onClose();
  }, [onSubmit, onClose]);

  const isReadOnly = mode === 'view';
  const label = primitiveType.charAt(0).toUpperCase() + primitiveType.slice(1);

  // Loading state
  if (schemaLoading) {
    const loadingContent = (
      <Stack gap="md">
        <Skeleton height={40} />
        <Skeleton height={60} />
        <Skeleton height={60} />
        <Skeleton height={40} width={120} />
      </Stack>
    );

    if (inline) return loadingContent;

    return (
      <Modal opened={opened} onClose={onClose} title={`Loading ${label} form...`} size="lg">
        {loadingContent}
      </Modal>
    );
  }

  // Error state
  if (error && !formWithData) {
    const errorContent = (
      <Alert color="red" title="Form Schema Error" icon={<IconAlertTriangle />}>
        Failed to load form schema for {label}: {error.message}
      </Alert>
    );

    if (inline) return errorContent;

    return (
      <Modal opened={opened} onClose={onClose} title={`${label} Form Error`} size="lg">
        {errorContent}
      </Modal>
    );
  }

  // No form definition available
  if (!formWithData) {
    const noFormContent = (
      <Alert color="yellow" title="Form Not Available" icon={<IconAlertTriangle />}>
        No form schema available for {label}. The backend needs to register a
        FormSchema for this primitive type.
      </Alert>
    );

    if (inline) return noFormContent;

    return (
      <Modal opened={opened} onClose={onClose} title={`${label} Form`} size="lg">
        {noFormContent}
      </Modal>
    );
  }

  const formContent = (
    <Stack gap="md">
      {/* Display Mode Selector */}
      <Group justify="space-between" align="center">
        <Group gap="xs">
          <IconFilter size={16} />
          <Text size="sm" fw={500}>Fields</Text>
        </Group>
        <Tooltip label={DISPLAY_MODE_DESCRIPTIONS[displayMode]}>
          <SegmentedControl
            size="xs"
            data={DISPLAY_MODE_DATA}
            value={displayMode}
            onChange={(val) => setDisplayMode(val as FieldDisplayMode)}
          />
        </Tooltip>
      </Group>

      {/* Safety level badge */}
      {formWithData.safetyLevel && formWithData.safetyLevel !== 'standard' && (
        <Badge
          color={formWithData.safetyLevel === 'life-critical' ? 'red' : 'orange'}
          leftSection={<IconShieldCheck size={12} />}
          variant="light"
        >
          {formWithData.safetyLevel}
        </Badge>
      )}

      {/* Backend-driven form */}
      <FormRenderer
        form={formWithData}
        initialValues={initialData as Record<string, unknown> ?? undefined}
        onSubmit={isReadOnly ? undefined : handleSubmit}
        onCancel={onClose}
      />

      {/* Action buttons (for view mode since FormRenderer won't show submit) */}
      {isReadOnly && (
        <>
          <Divider />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Close
            </Button>
          </Group>
        </>
      )}
    </Stack>
  );

  // Inline mode: render form content directly (for use inside Drawer)
  if (inline) {
    return formContent;
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`${mode === 'create' ? 'Create' : mode === 'edit' ? 'Edit' : 'View'} ${label}`}
      size="lg"
    >
      {formContent}
    </Modal>
  );
}
